import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { componentDestroyed, handleError, MediaApi, MediaItem, UntilDestroy } from '@mtgame/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogConfig, LoaderService, ToastService } from '@mtgame/ui';
import { TournamentTeamPhotosService } from './tournament-team-photos.service';
import { takeUntil } from 'rxjs/operators';
import { getGameMediaItemRoute } from '@config/routes';

@UntilDestroy
@Component({
  selector: 'mtg-photo-detail',
  templateUrl: './team-photo-detail.component.html',
  styleUrls: ['./team-photo-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamPhotoDetailComponent implements OnInit, OnDestroy {
  media: MediaItem;
  images: MediaItem[];
  loading: boolean;
  @HostBinding('class.fullscreen')
  isFullScreen: boolean;

  @HostListener('document:keydown.arrowRight', ['$event'])
  nextSlide() {
    if (this.nextImage) {
      this.openImage(this.nextImage);
    }
  }

  @HostListener('document:keydown.arrowLeft', ['$event'])
  previousSlide() {
    if (this.previousImage) {
      this.openImage(this.previousImage);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialogConfig: DialogConfig,
    private loaderService: LoaderService,
    private mediaApi: MediaApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private teamPhotosService: TournamentTeamPhotosService,
  ) {
  }

  ngOnInit(): void {
    this.teamPhotosService.media$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(images => {
        this.images = images;
        this.media = this.images.find(item => item.id === +this.route.snapshot.params.mediaId);
        this.cdr.markForCheck();
      });
    this.preloadImage();
  }

  ngOnDestroy(): void {
  }

  openImage(image: MediaItem): void {
    this.media = image;
    this.preloadImage();
    const urlParts = this.router.url.split('/');
    urlParts[urlParts.length - 1] = image.id.toString();
    this.router.navigateByUrl(urlParts.join('/'), {skipLocationChange: true});
  }

  preloadImage(): void {
    if (!this.media.isVideo) {
      const loading = {
        start: new Date().getTime(),
        end: null
      };
      setTimeout(() => {
        if (!loading.end) {
          loading.start = new Date().getTime();
          this.loading = true;
          this.cdr.markForCheck();
        }
      }, 100);
      this.cdr.markForCheck();
      const img = new Image();
      img.src = this.media.detailPhoto.path || this.media.originalPhoto.path;
      img.onload = () => {
        loading.end = new Date().getTime();
        if (loading.end - loading.start < 250) {
          setTimeout(() => {
            this.loading = false;
            this.cdr.markForCheck();
          }, 250 - loading.end + loading.start);
        } else {
          this.loading = false;
          this.cdr.markForCheck();
        }
      };
    }
  }

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  async download() {
    this.loaderService.show();

    try {
      const response = await this.mediaApi.downloadMedia(this.media.id);
      const fileUrl = URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = this.media.originalPhoto.name;
      a.click();
    } catch (e) {
      handleError(e);
      this.toastService.danger('Ошибка при скачивании фотографии');
    } finally {
      this.loaderService.hide();
    }
  }

  get shareUrl(): string {
    return `${window.location.origin}${getGameMediaItemRoute(this.media.gameId, this.media.id).join('/')}`;
  }

  get nextImage(): MediaItem {
    const mediaIndex = this.images.findIndex(i => i.id === this.media.id);
    if (mediaIndex === this.images.length - 4) {
      this.teamPhotosService.loadNextPage();
    }

    return mediaIndex < this.images.length - 1
      ? this.images[mediaIndex + 1]
      : null;
  }

  get previousImage(): MediaItem {
    const mediaIndex = this.images.findIndex(i => i.id === this.media.id);
    return mediaIndex > 0
      ? this.images[mediaIndex - 1]
      : null;
  }
}
